import React, { useEffect, useRef, useState } from 'react';

import ClearIcon from '@mui/icons-material/Clear';
import { Box, Button, Collapse, Tooltip, Typography } from '@mui/material';
import IconButton from '@mui/material/IconButton';

import { buildInitialAttachmentSlotStates } from './attachmentSlots';
import {
  ACCEPT_ATTRIBUTE,
  ACCEPTED_EXTENSIONS,
  ATTACHMENT_GUIDELINE_TEXT,
  DEFAULT_MAX_SIZE,
  VIDEO_EXTENSIONS,
  VIDEO_MAX_SIZE
} from './constants';
import { SectionHeading } from './SectionHeading';
import { classes } from './styles';
import { tooltipStyles } from '../NewUI/shared/commonStyles';

import type { AttachmentConfig, AttachmentSlotState, AttachmentText } from './types';

interface AttachmentsProps {
  attachments: AttachmentConfig[];
  attachmentText: AttachmentText;
  errorsByKey?: Record<string, string>;
  onAttachmentChange?: (attachmentKey: string) => void;
  onSlotStatesChange?: (states: Record<string, AttachmentSlotState>) => void;
  externalSlotOverride?: { key: string; slot: AttachmentSlotState } | null;
  isParsing?: boolean;
  fontFamily?: string;
}

const getFileExtension = (fileName: string): string =>
  fileName.split('.').pop()?.toLowerCase() ?? '';

const isVideoFile = (fileName: string): boolean => {
  const ext = getFileExtension(fileName);
  return VIDEO_EXTENSIONS.includes(ext);
};

const buildSlotMap = (attachments: AttachmentConfig[]): Map<string, AttachmentSlotState> =>
  new Map(Object.entries(buildInitialAttachmentSlotStates(attachments)));

const slotMapToRecord = (
  slots: Map<string, AttachmentSlotState>
): Record<string, AttachmentSlotState> => Object.fromEntries(slots);

export const Attachments: React.FC<AttachmentsProps> = ({
  attachments,
  attachmentText,
  errorsByKey = {},
  externalSlotOverride = null,
  isParsing = false,
  onAttachmentChange,
  onSlotStatesChange,
  fontFamily
}) => {
  const [slots, setSlots] = useState<Map<string, AttachmentSlotState>>(() =>
    buildSlotMap(attachments)
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const inputRefs = useRef<Map<string, HTMLInputElement | null>>(new Map());
  const onSlotStatesChangeRef = useRef(onSlotStatesChange);

  useEffect(() => {
    onSlotStatesChangeRef.current = onSlotStatesChange;
  }, [onSlotStatesChange]);

  useEffect(() => {
    onSlotStatesChangeRef.current?.(slotMapToRecord(slots));
  }, [slots]);

  const restoreInputFile = (attachmentKey: string, file: File | null) => {
    const input = inputRefs.current.get(attachmentKey);
    if (!input) return;

    if (!file) {
      input.value = '';
      return;
    }

    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    input.files = dataTransfer.files;
  };

  useEffect(() => {
    // Intentional: autofill and attachments are managed independently.
    // Clearing the autofill resume must not remove the mirrored attachment.
    if (!externalSlotOverride) {
      return;
    }

    const { key, slot } = externalSlotOverride;
    setSlots((prev) => {
      const existingSlot = prev.get(key);
      if (
        existingSlot?.pendingFile === slot.pendingFile &&
        existingSlot?.status === slot.status &&
        existingSlot?.displayName === slot.displayName
      ) {
        return prev;
      }

      const next = new Map(prev);
      next.set(key, slot);
      return next;
    });
    restoreInputFile(key, slot.pendingFile);
  }, [externalSlotOverride]);

  const handleFileChange = (
    attachment: AttachmentConfig,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (isParsing && attachment.isResume) {
      return;
    }

    const file = event.target.files?.[0] ?? null;
    const currentSlot = slots.get(attachment.key);

    if (file) {
      const extension = getFileExtension(file.name);

      if (!ACCEPTED_EXTENSIONS.includes(extension)) {
        setErrors((prev) => ({
          ...prev,
          [attachment.key]:
            'Unsupported file format. Use PDF, DOCX, DOC, JPG, PNG, MP4, MOV, or WEBM.'
        }));
        onAttachmentChange?.(attachment.key);
        restoreInputFile(attachment.key, currentSlot?.pendingFile ?? null);
        return;
      }

      const maxSize = isVideoFile(file.name) ? VIDEO_MAX_SIZE : DEFAULT_MAX_SIZE;

      if (file.size > maxSize) {
        const limitLabel = isVideoFile(file.name) ? '500MB' : '50MB';
        setErrors((prev) => ({
          ...prev,
          [attachment.key]: `File size exceeds ${limitLabel} limit`
        }));
        onAttachmentChange?.(attachment.key);
        restoreInputFile(attachment.key, currentSlot?.pendingFile ?? null);
        return;
      }

      setErrors((prev) => ({ ...prev, [attachment.key]: '' }));
      setSlots((prev) => {
        const next = new Map(prev);
        next.set(attachment.key, {
          ...next.get(attachment.key),
          status: 'selected',
          displayName: file.name,
          pendingFile: file
        });
        return next;
      });
      onAttachmentChange?.(attachment.key);
    }
  };

  const handleRemove = (attachment: AttachmentConfig) => {
    if (isParsing && attachment.isResume) {
      return;
    }

    const input = inputRefs.current.get(attachment.key);
    const slot = slots.get(attachment.key);

    if (input) {
      input.value = '';
    }

    setErrors((prev) => ({ ...prev, [attachment.key]: '' }));
    onAttachmentChange?.(attachment.key);

    if (slot?.status === 'selected' && slot.originalExisting) {
      const shouldRestoreRemovedExisting = slot.removedExisting;
      setSlots((prev) => {
        const next = new Map(prev);
        next.set(attachment.key, {
          status: shouldRestoreRemovedExisting ? 'empty' : 'existing',
          displayName: shouldRestoreRemovedExisting ? '' : (slot.originalExisting ?? ''),
          pendingFile: null,
          originalExisting: slot.originalExisting,
          removedExisting: shouldRestoreRemovedExisting
        });
        return next;
      });
      return;
    }

    if (slot?.status === 'existing' && slot.originalExisting) {
      setSlots((prev) => {
        const next = new Map(prev);
        next.set(attachment.key, {
          status: 'empty',
          displayName: '',
          pendingFile: null,
          originalExisting: slot.originalExisting,
          removedExisting: true
        });
        return next;
      });
      return;
    }

    setSlots((prev) => {
      const next = new Map(prev);
      next.set(attachment.key, {
        status: 'empty',
        displayName: '',
        pendingFile: null,
        originalExisting: slot?.originalExisting ?? null,
        removedExisting: slot?.removedExisting ?? false
      });
      return next;
    });
  };

  const activeSeekPrefillId = attachments.find(
    (attachment) =>
      attachment.seekPrefilled &&
      attachment.seekPrefillId &&
      slots.get(attachment.key)?.status === 'existing'
  )?.seekPrefillId;

  return (
    <Box
      aria-labelledby="attachments-heading"
      component="section"
      sx={classes.attachments.containerSx()}
    >
      <SectionHeading fontFamily={fontFamily} id="attachments-heading" variant="helperText">
        Attachments
      </SectionHeading>

      <Typography component="p" id="attachments-hint" sx={classes.attachments.globalHintSx()}>
        {ATTACHMENT_GUIDELINE_TEXT}
      </Typography>

      {activeSeekPrefillId ? (
        <input name="application[seek_prefill_id]" type="hidden" value={activeSeekPrefillId} />
      ) : null}

      {attachments.map((attachment) => {
        const errorMessage = errorsByKey[attachment.key] ?? errors[attachment.key];
        const slot = slots.get(attachment.key);
        const isResumeLocked = isParsing && attachment.isResume;
        return (
          <Box
            key={attachment.key}
            aria-describedby="attachments-hint"
            aria-labelledby={`attachment-label-${attachment.key}`}
            id={`attachment-group-${attachment.key}`}
            role="group"
            sx={classes.attachments.itemContainerSx()}
            tabIndex={-1}
          >
            <Typography
              component="label"
              htmlFor={`attachment-${attachment.key}`}
              id={`attachment-label-${attachment.key}`}
              sx={classes.attachments.labelSx()}
            >
              {attachment.name}
              {attachment.mandatory ? (
                <span aria-label="required" style={classes.shared.requiredIndicatorStyle}>
                  {' '}
                  *
                </span>
              ) : null}
            </Typography>

            {slot?.status === 'existing' ? (
              <>
                <input
                  name={attachment.paramName.replace(/]$/, '_filename]')}
                  type="hidden"
                  value={slot.displayName}
                />
                {attachment.seekPrefilled ? (
                  // `seek_attachment` mirrors Jobs::ApplyService::SEEK_ATTACHMENT_PARAM_KEY.
                  <input
                    name={`application[seek_attachment][${attachment.key}]`}
                    type="hidden"
                    value="1"
                  />
                ) : null}
              </>
            ) : null}
            {slot?.status === 'empty' && slot.removedExisting ? (
              <input
                name={`application[asset_remove][${attachment.key}]`}
                type="hidden"
                value="1"
              />
            ) : null}
            <Box sx={classes.attachments.fileInputRowSx()}>
              <Button
                aria-label={`Browse and upload ${attachment.name}`}
                component="label"
                disabled={isResumeLocked}
                sx={classes.attachments.browseButtonSx(!!errorMessage)}
              >
                Browse
                <input
                  ref={(el) => {
                    inputRefs.current.set(attachment.key, el);
                  }}
                  accept={ACCEPT_ATTRIBUTE}
                  aria-describedby={`attachments-hint attachment-status-${attachment.key}`}
                  aria-invalid={!!errorMessage}
                  aria-required={attachment.mandatory}
                  id={`attachment-${attachment.key}`}
                  name={attachment.paramName}
                  onChange={(e) => handleFileChange(attachment, e)}
                  style={classes.shared.validatableHiddenInputStyle}
                  tabIndex={-1}
                  type="file"
                />
              </Button>

              <Typography
                aria-atomic="true"
                aria-live="polite"
                component="span"
                id={`attachment-status-${attachment.key}`}
                role="status"
                sx={classes.attachments.fileNameDisplaySx()}
              >
                {slot?.displayName ? `Selected file: ${slot.displayName}` : ''}
              </Typography>

              {slot?.status !== 'empty' ? (
                <Tooltip
                  arrow
                  placement="top"
                  slotProps={{ tooltip: { sx: tooltipStyles } }}
                  title={attachmentText.removeFile}
                >
                  <span>
                    <IconButton
                      aria-label={`Remove ${attachment.name}`}
                      disabled={isResumeLocked}
                      onClick={() => handleRemove(attachment)}
                      sx={classes.attachments.removeButtonSx()}
                    >
                      <ClearIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : null}
            </Box>

            {/* unmountOnExit re-mounts the element so role="alert" announces each time. */}
            <Collapse unmountOnExit in={!!errorMessage}>
              <Box data-validation-error role="alert" sx={classes.attachments.errorSx()}>
                {errorMessage}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};

export default Attachments;
